<template>
  <div class="card mb-4">
    <div class="flex align-items-center justify-content-between mb-3">
      <div class="flex align-items-center">
        <router-link to="/mesServicesIA" class="no-underline mr-3">
          <Button icon="pi pi-arrow-left" class="p-button-text p-button-plain" />
        </router-link>
        <p class="text-4xl pl-2 text-700 my-0">{{ $t("servicesIA.generationTitle") }}</p>
      </div>

      <div class="flex gap-2 my-2 mr-3" id="ia-quota">
        <!-- Compteur quota journalier -->
        <div v-if="quota" class="flex align-items-center gap-3 border-1 border-round-xl px-3 py-2"
             :style="{ borderColor: quotaColor + '40', background: quotaColor + '0d' }">
          <div :style="quotaCircleStyle">
            <div style="width: 38px; height: 38px; border-radius: 50%; background: #ffffff;
                        display: flex; align-items: center; justify-content: center;">
              <span style="font-weight: 700; font-size: 0.875rem;" :style="{ color: quotaColor }">
                {{ quota.limit === -1 ? '∞' : quota.remaining }}
              </span>
            </div>
          </div>
          <div class="flex flex-column">
            <span class="font-bold text-sm" :style="{ color: quotaColor }">
              {{ quota.limit === -1 ? $t('servicesIA.quotaUnlimited') : $t('servicesIA.quotaRemaining', { n: quota.remaining }) }}
            </span>
            <span class="text-xs text-500" v-if="quota.limit !== -1">
              {{ $t('servicesIA.quotaOf', { used: quota.used, limit: quota.limit }) }}
            </span>
          </div>
        </div>

        <!-- Compteur crédits bonus -->
        <div v-if="quota && quota.bonus !== undefined" class="flex align-items-center gap-3 border-1 border-round-xl px-3 py-2"
             :style="{ borderColor: bonusColor + '40', background: bonusColor + '0d' }">
          <div :style="bonusCircleStyle">
            <div style="width: 38px; height: 38px; border-radius: 50%; background: #ffffff;
                        display: flex; align-items: center; justify-content: center;">
              <span style="font-weight: 700; font-size: 0.875rem;" :style="{ color: bonusColor }">
                {{ quota.bonus }}
              </span>
            </div>
          </div>
          <div class="flex flex-column">
            <span class="font-bold text-sm" :style="{ color: bonusColor }">
              {{ $t('servicesIA.bonusCredits', { n: quota.bonus }) }}
            </span>
            <span class="text-xs text-500">{{ $t('servicesIA.bonusCreditsHint') }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="h-full p-2">
      <TabView
        id="ia-tabs"
        v-model:activeIndex="activeTab"
        :pt="{
          panelContainer: () => ({ class: 'p-0' }),
        }"
      >
        <!-- ===== ONGLET 1 : FORMULAIRE GÉNÉRATION ===== -->
        <TabPanel
          :header="$t('servicesIA.generation')"
          :pt="{
            header: () => ({
              class: 'border-left-2 border-top-2 border-right-2 border-gray-300',
            }),
          }"
        >
          <div class="border-2 border-gray-300 border-top-none p-4">

            <!-- Message limite journalière atteinte -->
            <Message v-if="dailyLimitReached" severity="warn" :closable="false" class="mb-3">
              {{ $t("servicesIA.dailyLimitReached") }}
            </Message>

            <!-- Chargement d'une génération existante -->
            <div v-if="loadingExisting" class="flex justify-content-center p-4">
              <ProgressSpinner />
            </div>

            <!-- Formulaire -->
            <div v-if="!isProcessing && !isCompleted && !hasError && !loadingExisting">
              <div class="flex flex-column gap-4 md:w-8">

                <!-- Fichier spool (obligatoire) -->
                <div class="flex flex-column gap-1" id="ia-spool">
                  <label class="font-medium">
                    {{ $t("servicesIA.spoolFile") }}
                    <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    @change="onFileChange($event, 'spoolFile')"
                    :class="['p-inputtext w-full', { 'p-invalid': errors.spoolFile }]"
                    accept=".txt,.spool"
                  />
                  <small class="p-error" v-if="errors.spoolFile">
                    {{ $t("servicesIA.required") }}
                  </small>
                </div>

                <!-- PDF cible (obligatoire) -->
                <div class="flex flex-column gap-1" id="ia-target-pdf">
                  <label class="font-medium">
                    {{ $t("servicesIA.targetPdf") }}
                    <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    @change="onFileChange($event, 'targetPdf')"
                    :class="['p-inputtext w-full', { 'p-invalid': errors.targetPdf }]"
                    accept=".pdf"
                  />
                  <small class="p-error" v-if="errors.targetPdf">
                    {{ $t("servicesIA.required") }}
                  </small>
                </div>

                <!-- Fond de page (optionnel) -->
                <div class="flex flex-column gap-1" id="ia-fond-page">
                  <label class="font-medium">
                    {{ $t("servicesIA.backgroundPage") }}
                    <span class="text-500 text-sm"> {{ $t("servicesIA.optional") }}</span>
                  </label>
                  <input
                    type="file"
                    @change="onFileChange($event, 'fondPage')"
                    class="p-inputtext w-full"
                    accept=".pdf"
                  />
                </div>

                <!-- Nom du format (obligatoire) -->
                <div class="flex flex-column gap-1" id="ia-format-name">
                  <label class="font-medium">
                    {{ $t("servicesIA.formatNameField") }}
                    <span class="text-red-500">*</span>
                  </label>
                  <InputText
                    v-model="formatName"
                    :class="{ 'p-invalid': errors.formatName }"
                    class="w-full"
                    :placeholder="$t('servicesIA.formatNameField')"
                  />
                  <small class="p-error" v-if="errors.formatName">
                    {{ $t("servicesIA.required") }}
                  </small>
                </div>

                <!-- Type de document (optionnel) -->
                <div class="flex flex-column gap-1" id="ia-doc-type">
                  <label class="font-medium">
                    {{ $t("servicesIA.docType") }}
                    <span class="text-500 text-sm"> {{ $t("servicesIA.optional") }}</span>
                  </label>
                  <Dropdown
                    v-model="docType"
                    :options="docTypeOptions"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="$t('servicesIA.docType')"
                    class="w-full"
                    :showClear="true"
                  />
                </div>

                <div id="ia-btn-generer">
                  <Button
                    :label="$t('servicesIA.generate')"
                    icon="pi pi-play"
                    class="border-round-xl"
                    :disabled="dailyLimitReached"
                    @click="lancerGeneration"
                  />
                </div>
              </div>
            </div>

            <!-- Barre de progression (pendant le traitement) -->
            <div v-if="isProcessing || (isCompleted && segments.length > 0)" class="flex flex-column gap-3 md:w-8">
              <p v-if="isProcessing" class="text-lg font-medium text-700 my-0">{{ $t("servicesIA.processing") }}</p>

              <!-- File d'attente -->
              <div v-if="queuePosition !== null" class="flex align-items-center gap-2 text-600">
                <i class="pi pi-clock" />
                {{ $t("servicesIA.queued") }} ({{ $t("servicesIA.position") }} {{ queuePosition + 1 }})…
              </div>

              <!-- Barre segmentée -->
              <div v-if="queuePosition === null && visibleSegments.length > 0" class="flex flex-column gap-1">
                <!-- En-tête : label courant + % global -->
                <div class="flex justify-content-between text-600" style="font-size:.875rem">
                  <span>{{ currentStepLabel }}</span>
                  <span class="font-medium">{{ globalPct }}%</span>
                </div>

                <!-- Barre -->
                <div class="flex" style="height:1.25rem; border-radius:4px; overflow:hidden; gap:2px;">
                  <div
                    v-for="seg in visibleSegments"
                    :key="seg.key"
                    :style="{ flex: seg.flex, minWidth: '4px' }"
                    v-tooltip.top="seg.tooltip"
                    style="position:relative; overflow:hidden; border-radius:2px; cursor:default;"
                    :class="segBgClass(seg)"
                  >
                    <div
                      class="h-full"
                      :class="segFillClass(seg)"
                      :style="{ width: seg.fillPct + '%', transition: 'width 0.4s ease' }"
                    ></div>
                  </div>
                </div>

                <!-- Légende -->
                <div class="flex gap-3 text-500" style="font-size:.75rem;">
                  <span><span style="color:#4caf50">■</span> {{ $t("servicesIA.legendDone") }}</span>
                  <span><span style="color:#1a73e8">■</span> {{ $t("servicesIA.legendActive") }}</span>
                  <span><span style="color:#ccc">■</span> {{ $t("servicesIA.legendPending") }}</span>
                </div>
              </div>
            </div>

            <!-- Message d'erreur -->
            <div v-if="hasError" class="flex flex-column gap-3 md:w-8">
              <Message severity="error" :closable="false">
                {{ $t("servicesIA.errorOccurred") }}
              </Message>
              <Button
                :label="$t('servicesIA.retry')"
                icon="pi pi-refresh"
                class="border-round-xl w-auto"
                @click="resetForm"
              />
            </div>

            <!-- Message succès -->
            <div v-if="isCompleted && !hasError && !isWarning" class="flex flex-column gap-3 md:w-8">
              <Message severity="success" :closable="false">
                {{ $t("servicesIA.generationSuccess") }}
              </Message>
              <Button
                :label="$t('servicesIA.seeFiles')"
                icon="pi pi-folder-open"
                class="border-round-xl"
                @click="activeTab = 1"
              />
            </div>

            <!-- Message warning (max itérations) -->
            <div v-if="isCompleted && isWarning" class="flex flex-column gap-3 md:w-8">
              <Message severity="warn" :closable="false">
                {{ $t("servicesIA.maxIterationsWarning") }}
              </Message>
              <Button
                :label="$t('servicesIA.seeFiles')"
                icon="pi pi-folder-open"
                class="border-round-xl"
                @click="activeTab = 1"
              />
            </div>

          </div>
        </TabPanel>

        <!-- ===== ONGLET 2 : FICHIERS GÉNÉRÉS ===== -->
        <TabPanel
          :header="$t('servicesIA.generatedFiles')"
          :disabled="!isCompleted"
          :pt="{
            header: () => ({
              class: 'border-left-2 border-top-2 border-right-2 border-gray-300',
            }),
          }"
        >
          <div class="border-2 border-gray-300 border-top-none p-4">
            <div v-if="loadingFiles || loadingCompare" class="flex justify-content-center p-4">
              <ProgressSpinner />
            </div>

            <div v-else>
              <!-- Visionneuse PDF seule -->
              <div v-if="selectedPdfUrl && !compareMode" class="mb-4">
                <div class="flex justify-content-between align-items-center mb-2">
                  <span class="font-medium">{{ $t("servicesIA.pdfPreview") }}</span>
                  <Button
                    icon="pi pi-times"
                    class="p-button-text p-button-plain p-button-sm"
                    @click="selectedPdfUrl = null"
                  />
                </div>
                <iframe
                  :src="selectedPdfUrl"
                  class="w-full border-1 border-gray-300 border-round"
                  style="height: 60vh"
                ></iframe>
              </div>

              <!-- Vue comparaison côte à côte -->
              <div v-if="compareMode" class="mb-4">
                <div class="flex justify-content-between align-items-center mb-2">
                  <span class="font-medium">{{ $t("servicesIA.comparePdf") }}</span>
                  <Button
                    icon="pi pi-times"
                    class="p-button-text p-button-plain p-button-sm"
                    @click="closeCompare"
                  />
                </div>
                <div class="flex gap-2" style="height: 65vh">
                  <div
                    v-for="(c, index) in comparePdfUrls"
                    :key="c.iteration"
                    draggable="true"
                    @dragstart="onDragStart(index)"
                    @dragover.prevent="onDragOver(index)"
                    @drop.prevent="onDrop"
                    @dragend="dragOverIndex = null"
                    class="flex flex-column gap-1 min-w-0 border-round transition-all transition-duration-150"
                    :style="{
                      flex: 1,
                      cursor: 'grab',
                      outline: dragOverIndex === index && dragSourceIndex !== index ? '2px solid #1a73e8' : 'none',
                      opacity: dragSourceIndex === index ? '0.4' : '1',
                    }"
                  >
                    <span class="text-sm font-medium text-600 flex align-items-center gap-1">
                      <i class="pi pi-bars text-400" style="font-size: 0.75rem"></i>
                      {{ $t("servicesIA.iteration") }} {{ c.iteration }}
                    </span>
                    <iframe
                      :src="c.url"
                      class="w-full border-1 border-gray-300 border-round"
                      :style="{ flex: 1, minHeight: 0, pointerEvents: dragSourceIndex !== null ? 'none' : 'auto' }"
                    ></iframe>
                  </div>
                </div>
              </div>

              <!-- Barre d'actions : bouton comparer -->
              <div class="flex align-items-center justify-content-between mb-2" style="min-height: 2rem">
                <span class="text-sm text-500">
                  {{ $t("servicesIA.compareSelectHint") }}
                </span>
                <Button
                  v-if="canCompare"
                  :label="$t('servicesIA.compareSelected')"
                  icon="pi pi-clone"
                  class="p-button-sm p-button-outlined border-round-xl"
                  @click="startCompare"
                />
              </div>

              <DataTable
                :value="generatedFiles"
                dataKey="iteration"
                class="p-datatable-sm"
              >
                <template #empty>
                  <div class="p-3 text-center text-500">
                    {{ $t("servicesIA.noFiles") }}
                  </div>
                </template>

                <!-- Colonne checkbox manuelle (évite les conflits PrimeVue selectionMode) -->
                <Column headerStyle="width: 3rem">
                  <template #header>
                    <Checkbox
                      binary
                      :modelValue="allPdfSelected"
                      @update:modelValue="toggleAllSelection"
                    />
                  </template>
                  <template #body="slotProps">
                    <Checkbox
                      v-if="slotProps.data.pdf"
                      binary
                      :modelValue="selectedIterationIds.includes(slotProps.data.iteration)"
                      @update:modelValue="val => toggleRowSelection(slotProps.data.iteration, val)"
                    />
                  </template>
                </Column>
                <Column field="iteration" :header="$t('servicesIA.iteration')" sortable></Column>
                <Column :header="$t('servicesIA.pdfFile')">
                  <template #body="slotProps">
                    <Button
                      v-if="slotProps.data.pdf"
                      :label="$t('servicesIA.viewPdf')"
                      icon="pi pi-eye"
                      class="p-button-sm p-button-outlined border-round-xl"
                      @click="viewPdf(slotProps.data.pdf)"
                    />
                    <span v-else class="text-500">-</span>
                  </template>
                </Column>
                <Column :header="$t('servicesIA.jsonFile')">
                  <template #body="slotProps">
                    <Button
                      v-if="slotProps.data.json"
                      :label="$t('servicesIA.downloadJson')"
                      icon="pi pi-download"
                      class="p-button-sm p-button-outlined border-round-xl"
                      @click="downloadJson(slotProps.data.json)"
                    />
                    <span v-else class="text-500">-</span>
                  </template>
                </Column>
                <Column :header="$t('servicesIA.thumbnail')">
                  <template #body="slotProps">
                    <img
                      v-if="slotProps.data.thumbnail"
                      :src="slotProps.data.thumbnail"
                      alt="thumbnail"
                      style="height: 80px; width: auto; border-radius: 6px; border: 1px solid #e5e7eb; cursor: zoom-in; object-fit: contain;"
                      @click="zoomThumbnail = slotProps.data.thumbnail"
                    />
                    <span v-else class="text-500">-</span>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>
  </div>
  <Toast />

  <!-- Bouton visite guidée -->
  <Button
    class="fixed border-circle shadow-2 bg-blue-500 hover:bg-blue-700 text-white text-xl flex align-items-center justify-content-center"
    style="bottom: 20px; right: 20px; z-index: 9998; width: 3rem; height: 3rem;"
    @click="launchGuide('nouvelle_generation_ia')"
  >?</Button>

  <!-- Zoom thumbnail -->
  <div v-if="zoomThumbnail"
       style="position: fixed; inset: 0; background: rgba(0,0,0,0.75); z-index: 9999;
              display: flex; align-items: center; justify-content: center; cursor: zoom-out;"
       @click="zoomThumbnail = null">
    <img :src="zoomThumbnail" style="max-width: 90vw; max-height: 90vh; border-radius: 8px; box-shadow: 0 8px 32px rgba(0,0,0,0.5);" />
  </div>
</template>

<script>
import axios from "axios";
import { startTour } from "@/guide/guide";

export default {
  data() {
    return {
      // Onglet actif (0 = génération, 1 = fichiers)
      activeTab: 0,

      // Champs formulaire
      spoolFile: null,
      targetPdf: null,
      fondPage: null,
      formatName: "",
      docType: null,

      // Validation
      errors: {
        spoolFile: false,
        targetPdf: false,
        formatName: false,
      },

      // Quota journalier
      quota: null,

      // Zoom thumbnail
      zoomThumbnail: null,

      // État du workflow
      isProcessing: false,
      isCompleted: false,
      hasError: false,
      isWarning: false,
      dailyLimitReached: false,
      loadingExisting: false,

      // Progression
      currentStepLabel: "",
      progression: 0,
      pollingInterval: null,
      retryInterval: null,
      queuePosition: null,

      // Barre segmentée
      segments: [],
      currentIteration: 0,

      // Données en cours de génération
      generationId: null,

      // Fichiers générés
      generatedFiles: [],
      loadingFiles: false,
      selectedPdfUrl: null,

      // Comparaison PDF
      selectedIterationIds: [],
      compareMode: false,
      comparePdfUrls: [],
      loadingCompare: false,
      dragSourceIndex: null,
      dragOverIndex: null,

      // Options type document
      docTypeOptions: [],
    };
  },
  created() {
    // Pré-positionner loadingExisting avant le premier rendu pour éviter le flash du formulaire vide
    if (this.$route.query.id) {
      this.loadingExisting = true;
    }
  },

  mounted() {
    this.token = localStorage.getItem("token");
    this.fetchQuota();
    this.docTypeOptions = [
      { label: this.$t("servicesIA.invoice"),  value: "facture" },
      { label: this.$t("servicesIA.list"),     value: "liste"   },
      { label: this.$t("servicesIA.letter"),   value: "lettre"  },
      { label: this.$t("servicesIA.contract"), value: "contrat" },
    ];

    // Si on arrive depuis la liste (œil), charger la génération existante
    const idFromRoute = this.$route.query.id;
    if (idFromRoute) {
      this.loadingExisting = true;
      this.generationId = parseInt(idFromRoute);
      this.loadExistingGeneration();
    }
  },
  beforeUnmount() {
    this.stopPolling();
    this.stopRetrying();
    this.closeCompare();
  },
  computed: {
    quotaRemainingPct() {
      if (!this.quota || this.quota.limit === -1 || this.quota.limit <= 0) return 1;
      return Math.max(0, this.quota.remaining / this.quota.limit);
    },
    quotaColor() {
      if (!this.quota || this.quota.limit === -1) return '#22c55e';
      if (this.quota.remaining === 0) return '#1f2937';
      const pct = this.quotaRemainingPct;
      if (pct <= 0.2) return '#ef4444';
      if (pct <= 0.5) return '#f97316';
      return '#22c55e';
    },
    quotaCircleStyle() {
      const pct = Math.round(this.quotaRemainingPct * 100);
      const color = this.quotaColor;
      return {
        width: '56px', height: '56px', borderRadius: '50%', flexShrink: '0',
        background: 'conic-gradient(' + color + ' ' + pct + '%, #e5e7eb ' + pct + '%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      };
    },
    bonusColor() {
      if (!this.quota || this.quota.bonus === undefined) return '#a855f7';
      if (this.quota.bonus === 0) return '#1f2937';
      if (this.quota.bonus <= 3) return '#ef4444';
      if (this.quota.bonus <= 10) return '#f97316';
      return '#a855f7'; // violet pour distinguer du quota journalier
    },
    bonusCircleStyle() {
      const color = this.bonusColor;
      // Le cercle bonus est toujours "plein" — on montre juste la couleur sans arc de progression
      return {
        width: '56px', height: '56px', borderRadius: '50%', flexShrink: '0',
        background: 'conic-gradient(' + color + ' 100%, #e5e7eb 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      };
    },

    // Segments visibles : réels + placeholders jusqu'à MAX_ITER pendant le traitement
    visibleSegments() {
      if (this.isCompleted) return this.segments;
      const realIter = this.segments.filter(s => s.type === 'iteration').length;
      const placeholders = [];
      const maxIter = 10;
      for (let i = realIter + 1; i <= maxIter; i++) {
        placeholders.push({
          key: 'placeholder-' + i,
          type: 'placeholder',
          label: 'Itération ' + i,
          status: 'pending',
          fillPct: 0,
          flex: 1,
          tooltip: 'Itération ' + i + ' – En attente',
        });
      }
      return [...this.segments, ...placeholders];
    },

    // Pourcentage global basé sur les segments réels uniquement
    globalPct() {
      const real = this.segments;
      if (real.length === 0) return 0;
      const done   = real.filter(s => s.status === 'done').length;
      const active = real.find(s => s.status === 'active');
      const activePct = active ? active.fillPct / 100 : 0;
      return Math.round(((done + activePct) / real.length) * 100);
    },

    // Vrai quand au moins 2 itérations avec PDF sont sélectionnées
    canCompare() {
      return this.selectedIterationIds.length >= 2;
    },

    // Vrai quand toutes les lignes avec PDF sont sélectionnées
    allPdfSelected() {
      const withPdf = this.generatedFiles.filter(f => f.pdf);
      return withPdf.length > 0 && withPdf.every(f => this.selectedIterationIds.includes(f.iteration));
    },
  },

  methods: {
    launchGuide(context) {
      startTour(context, this.$i18n.locale);
    },
    fetchQuota() {
      axios.get(process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/quota", {
        headers: { Authorization: "Bearer " + this.token },
      }).then((res) => {
        this.quota = res.data;
      }).catch(() => {});
    },

    // Charge une génération existante (navigation depuis la liste)
    async loadExistingGeneration() {
      try {
        const res = await axios.get(
          process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/status/" + this.generationId,
          { headers: { Authorization: "Bearer " + this.token } }
        );
        const { step, label, progression, iteration } = res.data;
        this.currentStepLabel = label;
        this.progression = progression;

        // Reconstruire les segments depuis l'état courant (inclut les itérations précédentes)
        this.buildSegments(step, iteration ?? 0);

        if (step === 0) {
          this.isCompleted = true;
          this.fetchFiles();
        } else if (step === -2) {
          this.isWarning = true;
          this.isCompleted = true;
          this.fetchFiles();
        } else if (step === -1) {
          this.hasError = true;
        } else {
          this.isProcessing = true;
          this.startPolling();
        }
      } catch {
        this.hasError = true;
      } finally {
        this.loadingExisting = false;
      }
    },

    // -------------------------------------------------------------------------
    // Barre segmentée
    // -------------------------------------------------------------------------

    buildSegments(step, iteration) {
      const seg = this.segments;

      const findOrCreate = (key, type, label, flex) => {
        let s = seg.find(s => s.key === key);
        if (!s) {
          s = { key, type, label, status: 'pending', fillPct: 0, flex, tooltip: label + ' – En attente' };
          seg.push(s);
        }
        return s;
      };

      const setDone  = (key) => { const s = seg.find(s => s.key === key); if (s) { s.status = 'done';   s.fillPct = 100; s.tooltip = s.label + ' – Terminé ✓'; } };
      const setActive = (key, pct, tip) => { const s = seg.find(s => s.key === key); if (s) { s.status = 'active'; s.fillPct = pct; s.tooltip = tip; } };

      // Segments globaux (créés dès le départ si absents)
      findOrCreate('init',   'global', 'Initialisation', 1);
      findOrCreate('script', 'global', 'Génération du script', 1);

      switch (step) {
        case 10:
          setActive('init', 50, 'Initialisation – En cours…');
          break;

        case 20:
          setDone('init');
          setActive('script', 50, 'Génération du script – En cours…');
          break;

        case 30: {
          setDone('init');
          setDone('script');
          for (let i = 1; i < iteration; i++) {
            findOrCreate('iter-' + i, 'iteration', 'Itération ' + i, 2);
            setDone('iter-' + i);
          }
          const iterKey = 'iter-' + iteration;
          findOrCreate(iterKey, 'iteration', 'Itération ' + iteration, 2);
          setActive(iterKey, 20, 'Itération ' + iteration + ' – Insertion en base…');
          break;
        }

        case 40: {
          setDone('init');
          setDone('script');
          for (let i = 1; i < iteration; i++) {
            findOrCreate('iter-' + i, 'iteration', 'Itération ' + i, 2);
            setDone('iter-' + i);
          }
          const iterKey = 'iter-' + iteration;
          findOrCreate(iterKey, 'iteration', 'Itération ' + iteration, 2);
          setActive(iterKey, 55, 'Itération ' + iteration + ' – Génération PDF…');
          break;
        }

        case 50: {
          setDone('init');
          setDone('script');
          for (let i = 1; i < iteration; i++) {
            findOrCreate('iter-' + i, 'iteration', 'Itération ' + i, 2);
            setDone('iter-' + i);
          }
          const iterKey = 'iter-' + iteration;
          findOrCreate(iterKey, 'iteration', 'Itération ' + iteration, 2);
          setActive(iterKey, 85, 'Itération ' + iteration + ' – Analyse IA…');
          break;
        }

        case -2:
        case 0:
          // Créer toutes les itérations connues puis tout marquer terminé
          setDone('init');
          setDone('script');
          for (let i = 1; i <= iteration; i++) {
            findOrCreate('iter-' + i, 'iteration', 'Itération ' + i, 2);
          }
          seg.forEach(s => { s.status = 'done'; s.fillPct = 100; s.tooltip = s.label + ' – Terminé ✓'; });
          break;

        case -1:
          // Erreur sur le segment actif
          { const active = seg.find(s => s.status === 'active');
            if (active) { active.status = 'error'; active.tooltip = active.label + ' – Erreur ✗'; } }
          break;
      }
    },

    segBgClass(seg) {
      return {
        'seg-done':    seg.status === 'done',
        'seg-active':  seg.status === 'active',
        'seg-pending': seg.status === 'pending',
        'seg-error':   seg.status === 'error',
      };
    },

    segFillClass(seg) {
      return {
        'seg-fill-done':   seg.status === 'done',
        'seg-fill-active': seg.status === 'active',
        'seg-fill-error':  seg.status === 'error',
      };
    },

    // Extrait le paramètre "file" depuis l'URL de download fournie par l'API spool
    // ex: "/download?format=T00001440&file=export_json%2Fconfig_1.json" → "export_json/config_1.json"
    extractFilePath(url) {
      try {
        const params = new URLSearchParams(url.split('?')[1] || '');
        return decodeURIComponent(params.get('file') || '');
      } catch {
        return '';
      }
    },

    onFileChange(event, field) {
      const file = event.target.files[0] || null;
      this[field] = file;
      if (file) this.errors[field] = false;
    },

    validate() {
      this.errors.spoolFile  = !this.spoolFile;
      this.errors.targetPdf  = !this.targetPdf;
      this.errors.formatName = !this.formatName.trim();
      return !this.errors.spoolFile && !this.errors.targetPdf && !this.errors.formatName;
    },

    async lancerGeneration() {
      if (!this.validate()) return;

      this.isProcessing = true;
      this.hasError = false;
      this.progression = 0;

      try {
        // Étape 1 : POST /init
        const initResponse = await axios.post(
          process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/init",
          {
            format_name: this.formatName,
            type: this.docType || "",
            client: localStorage.getItem("nomEntreprise") || "",
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: "Bearer " + this.token,
            },
          }
        );

        this.generationId = initResponse.data.id;
        this.currentStepLabel = this.$t("servicesIA.stepInit");

        // Étape 2 : démarrer le polling AVANT generate (le spool est synchrone)
        this.startPolling();

        const formData = new FormData();
        formData.append("spool", this.spoolFile);
        formData.append("target_pdf", this.targetPdf);
        if (this.fondPage) {
          formData.append("fond_page", this.fondPage);
        }

        const generateResponse = await axios.post(
          process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/generate/" + this.generationId,
          formData,
          { headers: { Authorization: "Bearer " + this.token } }
        );

        // Si le polling a déjà géré la fin, ne pas re-traiter
        if (this.isCompleted || this.hasError) return;

        const generateStatus = generateResponse.data.status;

        if (generateStatus === "queued") {
          this.stopPolling();
          this.startRetrying();
        } else if (generateStatus === "done") {
          this.stopPolling();
          this.buildSegments(0, 0);
          this.isProcessing = false;
          this.isCompleted = true;
          this.fetchFiles();
        } else if (generateStatus === "warning") {
          this.stopPolling();
          this.buildSegments(-2, 0);
          this.isWarning = true;
          this.isProcessing = false;
          this.isCompleted = true;
          this.fetchFiles();
        } else if (generateStatus === "error") {
          this.stopPolling();
          this.isProcessing = false;
          this.hasError = true;
        }
        // "running" → polling déjà actif, on laisse faire

      } catch (e) {
        if (e?.response?.status === 429) {
          this.dailyLimitReached = true;
          this.isProcessing = false;
        } else if (e?.response?.status === 401) {
          this.$router.push("/login");
        } else {
          this.isProcessing = false;
          this.hasError = true;
        }
      }
    },

    startRetrying() {
      this.retryInterval = setInterval(async () => {
        try {
          const res = await axios.post(
            process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/generate/" + this.generationId + "/retry",
            {},
            { headers: { Authorization: "Bearer " + this.token } }
          );
          const status = res.data.status;
          if (status === "running") {
            this.stopRetrying();
            this.startPolling();
          } else if (status === "done") {
            this.stopRetrying();
            this.isProcessing = false;
            this.isCompleted = true;
            this.fetchFiles();
          } else if (status === "error") {
            this.stopRetrying();
            this.isProcessing = false;
            this.hasError = true;
          }
          // 'queued' → continuer à attendre
        } catch {
          // Erreur réseau silencieuse pendant le retry
        }
      }, 5000);
    },

    stopRetrying() {
      if (this.retryInterval !== null) {
        clearInterval(this.retryInterval);
        this.retryInterval = null;
      }
    },

    startPolling() {
      this.pollingInterval = setInterval(async () => {
        try {
          const res = await axios.get(
            process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/status/" + this.generationId,
            { headers: { Authorization: "Bearer " + this.token } }
          );

          const { step, label, progression, iteration, queue_status, queue_position } = res.data;

          if (queue_status === "WAITING") {
            this.queuePosition = queue_position;
            this.currentStepLabel = this.$t("servicesIA.queued");
            return;
          }

          this.queuePosition = null;
          this.currentStepLabel = label;
          this.progression = progression;
          this.buildSegments(step, iteration ?? 0);

          if (step === 0) {
            this.stopPolling();
            this.isProcessing = false;
            this.isCompleted = true;
            this.fetchFiles();
          } else if (step === -2) {
            this.stopPolling();
            this.isWarning = true;
            this.isProcessing = false;
            this.isCompleted = true;
            this.fetchFiles();
          } else if (step === -1) {
            this.stopPolling();
            this.isProcessing = false;
            this.hasError = true;
          }
        } catch {
          this.stopPolling();
          this.isProcessing = false;
          this.hasError = true;
        }
      }, 1000);
    },

    stopPolling() {
      if (this.pollingInterval !== null) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    async fetchFiles() {
      this.selectedIterationIds = [];
      this.closeCompare();
      this.loadingFiles = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/files/" + this.generationId,
          {
            headers: { Authorization: "Bearer " + this.token },
          }
        );

        // L'API retourne [{iteration, pdf: {filename, url}, json: {filename, url}}, ...]
        // On extrait le paramètre "file" de l'url pour l'appel /download
        const rawFiles = res.data.files || res.data || [];

        this.generatedFiles = rawFiles
          .map((f) => ({
            iteration: f.iteration,
            pdf:       f.pdf       ? this.extractFilePath(f.pdf.url)  : null,
            json:      f.json      ? this.extractFilePath(f.json.url) : null,
            thumbnail: f.thumbnail || null,
          }))
          .sort((a, b) => b.iteration - a.iteration);
        this.activeTab = 1;
      } catch {
        this.$toast.add({
          severity: "warn",
          summary: this.$t("erreur.erreur"),
          detail: this.$t("servicesIA.errorLoadFiles"),
          life: 3000,
        });
      } finally {
        this.loadingFiles = false;
      }
    },

    async viewPdf(filePath) {
      this.closeCompare();
      try {
        const response = await axios.get(
          process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/download/" + this.generationId,
          {
            params: { file: filePath },
            headers: { Authorization: "Bearer " + this.token },
            responseType: "blob",
          }
        );
        const blob = new Blob([response.data], { type: "application/pdf" });
        this.selectedPdfUrl = URL.createObjectURL(blob);
      } catch {
        this.$toast.add({
          severity: "error",
          summary: this.$t("erreur.erreur"),
          detail: this.$t("erreur.erreurInterne"),
          life: 3000,
        });
      }
    },

    // -------------------------------------------------------------------------
    // Comparaison PDF
    // -------------------------------------------------------------------------

    toggleRowSelection(iterationId, selected) {
      if (selected) {
        if (!this.selectedIterationIds.includes(iterationId)) {
          this.selectedIterationIds.push(iterationId);
        }
      } else {
        this.selectedIterationIds = this.selectedIterationIds.filter(id => id !== iterationId);
      }
    },

    toggleAllSelection(selected) {
      if (selected) {
        this.selectedIterationIds = this.generatedFiles.filter(f => f.pdf).map(f => f.iteration);
      } else {
        this.selectedIterationIds = [];
      }
    },

    async startCompare() {
      const toCompare = this.generatedFiles.filter(f =>
        this.selectedIterationIds.includes(f.iteration) && f.pdf
      );
      if (toCompare.length < 2) return;

      this.selectedPdfUrl = null;
      this.loadingCompare = true;
      try {
        const urls = await Promise.all(
          toCompare.map(async f => {
            const response = await axios.get(
              process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/download/" + this.generationId,
              {
                params: { file: f.pdf },
                headers: { Authorization: "Bearer " + this.token },
                responseType: "blob",
              }
            );
            const blob = new Blob([response.data], { type: "application/pdf" });
            return { iteration: f.iteration, url: URL.createObjectURL(blob) };
          })
        );
        this.comparePdfUrls = urls;
        this.compareMode = true;
      } catch {
        this.$toast.add({
          severity: "error",
          summary: this.$t("erreur.erreur"),
          detail: this.$t("erreur.erreurInterne"),
          life: 3000,
        });
      } finally {
        this.loadingCompare = false;
      }
    },

    closeCompare() {
      this.comparePdfUrls.forEach(c => { if (c.url) URL.revokeObjectURL(c.url); });
      this.comparePdfUrls = [];
      this.compareMode = false;
      this.dragSourceIndex = null;
      this.dragOverIndex = null;
    },

    onDragStart(index) {
      this.dragSourceIndex = index;
    },

    onDragOver(index) {
      this.dragOverIndex = index;
    },

    onDrop() {
      const from = this.dragSourceIndex;
      const to   = this.dragOverIndex;
      if (from === null || to === null || from === to) return;
      const items = [...this.comparePdfUrls];
      const [moved] = items.splice(from, 1);
      items.splice(to, 0, moved);
      this.comparePdfUrls = items;
      this.dragSourceIndex = null;
      this.dragOverIndex = null;
    },

    async downloadJson(filePath) {
      try {
        const response = await axios.get(
          process.env.VUE_APP_URL_BACK_END + "/api/servicesIA/download/" + this.generationId,
          {
            params: { file: filePath },
            headers: { Authorization: "Bearer " + this.token },
            responseType: "blob",
          }
        );
        const blob = new Blob([response.data], { type: "application/json" });
        const url  = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filePath.split("/").pop() || "config.json";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch {
        this.$toast.add({
          severity: "error",
          summary: this.$t("erreur.erreur"),
          detail: this.$t("erreur.erreurInterne"),
          life: 3000,
        });
      }
    },

    resetForm() {
      this.spoolFile     = null;
      this.targetPdf     = null;
      this.fondPage      = null;
      this.formatName    = "";
      this.docType       = null;
      this.isProcessing  = false;
      this.isCompleted   = false;
      this.hasError      = false;
      this.progression   = 0;
      this.generationId  = null;
      this.generatedFiles = [];
      this.selectedPdfUrl = null;
      this.queuePosition = null;
      this.isWarning = false;
      this.segments = [];
      this.currentIteration = 0;
      this.selectedIterationIds = [];
      this.closeCompare();
      this.errors = { spoolFile: false, targetPdf: false, formatName: false };
      // Réinitialiser les inputs fichier
      document.querySelectorAll("input[type='file']").forEach((el) => (el.value = ""));
    },
  },
};
</script>

<style scoped>
/* Segments de la barre de progression */
.seg-done    { background: #e8f5e9; }
.seg-active  { background: #e3f2fd; }
.seg-pending { background: #f0f0f0; }
.seg-error   { background: #fce8e6; }

.seg-fill-done   { background: #4caf50; }
.seg-fill-active { background: #1a73e8; }
.seg-fill-error  { background: #ea4335; }
</style>
